// src/api/soportesTecnicosApi.ts

import { getAuthToken } from '../security/AuthLogin';

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
const API_URL = `${BASE_URL}/api`;

export interface SoporteTecnico {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  fecha_publicacion?: string;
  enlace?: string;
  cliente_nombre?: string;
  cliente_email?: string;
  fecha_alta_soporte?: string;
  estado_soporte?: string | null;
  dueno_id?: string | null;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
  id_soporte?: number;

  // Campos de mensajería:
  leido?: number;                    // 0 o 1
  ultimo_emisor?: string | null;     // 'nadie' | 'cliente' | 'admin_tecnico' | etc.
  ultima_fecha_msg?: string | null;

  importe_total?: number;
  importe_estimado?: number;
}

/**
 * Respuesta que esperamos del backend al pedir el detalle.
 */
export interface SoporteTecnicoDetalleResponse {
  success: boolean;
  soporte_tecnico?: SoporteTecnico;
  message?: string;
}

/** Parámetros opcionales para listar soportes técnicos. */
export interface FetchSoportesTecnicosParams {
  page?: number;
  limit?: number;
  search?: string;
  fechaInicio?: string;
  fechaFin?: string;
  importeDesde?: string;
  importeHasta?: string;
  autor?: string;
  asignado?: string;
  cerrados?: string;
  noRespondidos?: string;
}

/** Respuesta al listar soportes técnicos. */
interface FetchSoportesTecnicosResponse {
  data: SoporteTecnico[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Lista de soportes técnicos (GET /api/soportes-tecnicos).
 * Devuelve un objeto con { data, total, page, limit }.
 */
export const fetchSoportesTecnicos = async (
  params?: FetchSoportesTecnicosParams,
  signal?: AbortSignal
): Promise<FetchSoportesTecnicosResponse> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const queryString = new URLSearchParams();
  if (params?.page) queryString.set('page', params.page.toString());
  if (params?.limit) queryString.set('limit', params.limit.toString());
  if (params?.search) queryString.set('search', params.search);
  if (params?.fechaInicio) queryString.set('fechaInicio', params.fechaInicio);
  if (params?.fechaFin) queryString.set('fechaFin', params.fechaFin);
  if (params?.importeDesde) queryString.set('importeDesde', params.importeDesde);
  if (params?.importeHasta) queryString.set('importeHasta', params.importeHasta);
  if (params?.autor) queryString.set('autor', params.autor);
  if (params?.asignado) queryString.set("asignado", params.asignado);
  if (params?.cerrados) queryString.set('cerrados', params.cerrados);
  if (params?.noRespondidos)
    queryString.set("noRespondidos", params.noRespondidos);

  const url = `${API_URL}/soportes-tecnicos?${queryString.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al obtener soportes técnicos: ${errorText}`);
  }

  return response.json();
};

/**
 * Detalle de un soporte técnico (GET /api/soportes-tecnicos/detalle/:id_concurso/:id_usuario).
 * El backend devuelve { success, soporte_tecnico, message }.
 */
export const fetchSoporteTecnicoDetalle = async (
  idConcurso: string,
  idUsuario: string
): Promise<SoporteTecnicoDetalleResponse> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const url = `${API_URL}/soportes-tecnicos/detalle/${idConcurso}/${idUsuario}`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al obtener detalle del soporte técnico: ${errorText}`);
  }

  // El backend retorna algo como: { success: true, soporte_tecnico, message }
  const data = await response.json() as SoporteTecnicoDetalleResponse;
  return data;
};
